import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Forbidden");
}

function normalizePath(raw: string): string {
  try {
    let p = raw.trim();
    if (!p) return "/";
    // strip origin if present
    if (/^https?:\/\//i.test(p)) {
      const u = new URL(p);
      p = u.pathname + u.search;
    }
    // drop query/hash for matching key
    p = p.split("#")[0].split("?")[0];
    if (!p.startsWith("/")) p = "/" + p;
    // collapse trailing slash (except root)
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    return p.toLowerCase();
  } catch {
    return "/";
  }
}

// -------- Public: log a 404 (no auth) --------
export const logNotFound = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        path: z.string().min(1).max(2048),
        referrer: z.string().max(2048).optional().nullable(),
        userAgent: z.string().max(1000).optional().nullable(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const path = normalizePath(data.path);
    if (!path || path === "/") return { ok: true };
    // skip noise
    if (/\.(png|jpg|jpeg|gif|svg|ico|css|js|map|webp|woff2?)$/i.test(path)) {
      return { ok: true };
    }

    const { data: existing } = await supabaseAdmin
      .from("not_found_log")
      .select("id, hits")
      .eq("path", path)
      .maybeSingle();

    if (existing) {
      await supabaseAdmin
        .from("not_found_log")
        .update({
          hits: existing.hits + 1,
          last_seen_at: new Date().toISOString(),
          last_referrer: data.referrer ?? null,
          last_user_agent: data.userAgent ?? null,
        })
        .eq("id", existing.id);
    } else {
      await supabaseAdmin.from("not_found_log").insert({
        path,
        hits: 1,
        last_referrer: data.referrer ?? null,
        last_user_agent: data.userAgent ?? null,
      });
    }
    return { ok: true };
  });

// -------- Public: lookup a redirect (no auth, used by root beforeLoad) --------
export const lookupRedirect = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({ path: z.string().min(1).max(2048) }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const path = normalizePath(data.path);
    const { data: row } = await supabaseAdmin
      .from("redirects")
      .select("id, to_path, status_code, enabled")
      .eq("from_path", path)
      .eq("enabled", true)
      .maybeSingle();
    if (!row) return { match: null };
    // fire-and-forget usage bump
    void supabaseAdmin
      .from("redirects")
      .update({ last_used_at: new Date().toISOString() })
      .eq("id", row.id);
    return {
      match: { to: row.to_path, status: row.status_code as number },
    };
  });


// -------- Admin: list 404 log --------
export const listNotFound = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        search: z.string().trim().max(200).optional().or(z.literal("")),
        sinceDays: z.number().int().min(1).max(365).optional(),
        limit: z.number().int().min(1).max(500).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.supabase, context.userId);
    let q = supabaseAdmin
      .from("not_found_log")
      .select("id, path, hits, last_seen_at, first_seen_at, last_referrer, last_user_agent, last_ip")
      .order("last_seen_at", { ascending: false })
      .limit(data.limit ?? 200);

    if (data.sinceDays) {
      const since = new Date(Date.now() - data.sinceDays * 86400_000).toISOString();
      q = q.gte("last_seen_at", since);
    }
    if (data.search) {
      q = q.ilike("path", `%${data.search}%`);
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

export const deleteNotFound = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid() }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.supabase, context.userId);
    const { error } = await supabaseAdmin.from("not_found_log").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// -------- Admin: redirect CRUD --------
export const listRedirects = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await supabaseAdmin
      .from("redirects")
      .select("id, from_path, to_path, status_code, enabled, note, hits, last_used_at, created_at, updated_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { rows: data ?? [] };
  });

export const upsertRedirect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid().optional(),
        from_path: z.string().min(1).max(2048),
        to_path: z.string().min(1).max(2048),
        status_code: z.number().int().refine((v) => [301, 302, 307, 308].includes(v)).optional(),
        enabled: z.boolean().optional(),
        note: z.string().max(500).optional().nullable(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.supabase, context.userId);
    const from_path = normalizePath(data.from_path);
    const to_path = data.to_path.startsWith("/") || /^https?:\/\//i.test(data.to_path)
      ? data.to_path.trim()
      : "/" + data.to_path.trim();

    const payload = {
      from_path,
      to_path,
      status_code: data.status_code ?? 301,
      enabled: data.enabled ?? true,
      note: data.note ?? null,
    };

    if (data.id) {
      const { error } = await supabaseAdmin.from("redirects").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }

    const { data: row, error } = await supabaseAdmin
      .from("redirects")
      .upsert(payload, { onConflict: "from_path" })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteRedirect = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertAdmin(context.supabase, context.userId);
    const { error } = await supabaseAdmin.from("redirects").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
